import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import AuthLayout, { FormError, PasswordInput, TextLink } from '../components/AuthLayout';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        err.response?.status === 401 || !detail
          ? "That email and password don't match. Check for typos, or reset your password."
          : detail
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Pick up where you left off with your inspections."
      footer={<>New to RealSpect? <TextLink to="/register" data-testid="go-to-register">Create an account</TextLink></>}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate={false}>
        <FormError>{error}</FormError>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="h-12"
            aria-invalid={!!error}
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <Label htmlFor="login-password">Password</Label>
            <TextLink to="/forgot-password" data-testid="forgot-password-link">
              <span className="text-sm">Forgot password?</span>
            </TextLink>
          </div>
          <PasswordInput
            id="login-password"
            data-testid="login-password-input"
            toggleTestId="toggle-password-visibility"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            aria-invalid={!!error}
            required
          />
        </div>

        <Button data-testid="login-submit-button" type="submit" size="lg" disabled={loading} className="mt-1 w-full">
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
